import { useEffect, useState, useLayoutEffect } from 'react';

import { createResizeObserver } from '../utils/observer';
import { areEqual } from '../utils/iterable/areEqual';
import { getSize } from '../utils/iterable/getSize';
import { reduce } from '../utils/iterable/reduce';

import { SEPARATOR } from '../constants';

const INITIAL_SIZE = { height: 0, width: 0 };
const INITIAL_OVERFLOW = { x: false, y: false };

export function useMediaQuery({ enabled = true, query }) {
  function mediaQuery() {
    return window.matchMedia(query);
  }

  function getInitialState() {
    return mediaQuery().matches;
  }

  const [state, setState] = useState(getInitialState);

  useEffect(
    function effect() {
      const mediaQueryList = mediaQuery();

      function changeState(event) {
        setState(event.matches);
      }

      function cleanUp() {
        try {
          mediaQueryList.removeEventListener('change', changeState);
        } catch (error) {
          mediaQueryList.removeListener(changeState);
        }
      }

      if (!enabled) return cleanUp();

      try {
        mediaQueryList.addEventListener('change', changeState);
      } catch (error) {
        mediaQueryList.addListener(changeState);
      }

      return cleanUp;
    },
    [enabled]
  );

  return state;
}

export function useWindowSize() {
  const [state, setState] = useState(INITIAL_SIZE);

  function onResize() {
    setState({
      height: window.innerHeight,
      width: window.innerWidth,
    });
  }

  useLayoutEffect(
    function effect() {
      onResize();
      window.addEventListener('resize', onResize);

      return function cleanUp() {
        window.removeEventListener('resize', onResize);
      };
    },
    []
  );

  return state;
}

export function useElementSize({ boundings = 'offset', dependencies = [], ref }) {
  function getInitialState() {
    function reduceBoundings(result, bounding) {
      result[bounding] = INITIAL_SIZE;
      return result;
    }

    if (Array.isArray(boundings)) {
      return boundings.reduce(reduceBoundings, {});
    } else {
      return INITIAL_SIZE;
    }
  }

  const [state, setState] = useState(getInitialState);

  useLayoutEffect(
    function effect() {
      if (!ref.current) {
        const initialState = getInitialState();

        if (state !== initialState) {
          setState(initialState);
        }

        return undefined;
      }

      function onResize() {
        if (!ref.current) return;

        function getDimensions(bounding) {
          const { [`${bounding}Height`]: height, [`${bounding}Width`]: width } = ref.current;
          return { height, width };
        }

        function reduceBoundings(result, bounding) {
          result[bounding] = getDimensions(bounding);
          return result;
        }

        function getState() {
          if (Array.isArray(boundings)) {
            return boundings.reduce(reduceBoundings, {});
          } else {
            return getDimensions(boundings);
          }
        }

        setState(function updateState(oldState) {
          const newState = getState();
          return areEqual(newState, oldState)
            ? oldState
            : newState;
        });
      }

      const observer = createResizeObserver(onResize);

      observer.observe(ref.current);

      return function cleanUp() {
        observer.disconnect();
      };
    },
    [boundings, ref.current, ...dependencies]
  );

  return state;
}

export function useElementOverflow({ ref }) {
  const [state, setState] = useState(INITIAL_OVERFLOW);

  useLayoutEffect(
    function effect() {
      if (!ref.current) {
        if (state !== INITIAL_OVERFLOW) {
          setState(INITIAL_OVERFLOW);
        }

        return undefined;
      }

      function onResize() {
        const { clientHeight, clientWidth, scrollHeight, scrollWidth } = ref.current;
        const x = scrollWidth > clientWidth;
        const y = scrollHeight > clientHeight;
        setState(function updateState(oldState) {
          return oldState.x === x && oldState.y === y
            ? oldState
            : { x, y };
        });
      }

      const observer = createResizeObserver(onResize);

      observer.observe(ref.current);

      return function cleanUp() {
        observer.disconnect();
      };
    },
    [ref.current]
  );

  return state;
}

export function useTextOverflow({ ref, separator = SEPARATOR.COMMA, words }) {
  const [overflow, setOverflow] = useState(0);

  useLayoutEffect(
    function effect() {
      if (getSize(words) === 0 || !ref.current || ref.current.scrollWidth <= ref.current.clientWidth) {
        if (overflow) {
          setOverflow(0);
        }

        return;
      }

      const separatorSize = getSize(separator);
      const clientRect = ref.current.getBoundingClientRect();
      const clientLeft = clientRect.left + clientRect.width;
      const clone = document.createElement('span');
      clone.style.position = 'absolute';
      clone.style.inset = '0';
      clone.style.visibility = 'hidden';
      clone.style.whiteSpace = 'nowrap';
      clone.innerText = ref.current.innerText;
      ref.current.appendChild(clone);
      const text = clone.childNodes[0];
      const range = document.createRange();

      function reduceWords(result, word) {
        if (result.overflow > 0) {
          result.overflow += 1;
          return result;
        }

        result.end = result.start + getSize(word);

        range.setStart(text, result.start);
        range.setEnd(text, result.end);
        const wordRect = range.getBoundingClientRect();

        if (wordRect.left + wordRect.width > clientLeft) {
          result.overflow += 1;
        }

        result.start = result.end + separatorSize;
        return result;
      }

      const reduced = reduce(words, { end: 0, overflow: 0, start: 0 }, reduceWords);

      if (reduced.overflow !== overflow) {
        setOverflow(reduced.overflow);
      }

      ref.current.removeChild(clone);
    },
    [ref, separator, words, overflow],
  );

  return overflow;
}
